import { useEffect, useMemo, useState } from "react";
import {
  computeStepTestRows,
  rollupStepTestStatus,
  type StepTestDotStatus,
  type StepTestRow,
  stepContentHashFromFiles,
  testIoHashFromFiles,
} from "../../shared/stepTests/freshness";

type CommitFile = { readonly path: string; readonly blobHash: string };

type Spec = {
  readonly name: string;
  readonly status: "passed" | "failed" | "skipped";
  readonly message: string | null;
  readonly inputOutputHash: string | null;
};

type Execution = {
  readonly stepDir: string | null;
  readonly status: "pending" | "success" | "error";
  readonly codeHash: string | null;
  readonly testSpecs: readonly Spec[];
};

// Resolves each step test's dot status: derives the step's current code and
// per-test input/output hashes from the commit files, then matches them
// against the run history (see computeStepTestRows). `couldNotRun` is true when
// the current run errored without producing any per-test results.
export function useStepTestRows({
  commitFiles,
  stepDir,
  testNames,
  executions,
}: {
  commitFiles: readonly CommitFile[];
  stepDir: string;
  testNames: readonly string[];
  executions: readonly Execution[];
}): {
  rows: StepTestRow[];
  rollup: StepTestDotStatus;
  couldNotRun: boolean;
  ready: boolean;
} {
  const stepExecutions = useMemo(
    () => executions.filter((e) => e.stepDir === stepDir),
    [executions, stepDir]
  );

  const [hashes, setHashes] = useState<{
    codeHash: string;
    ioHashByName: Map<string, string>;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const codeHash = await stepContentHashFromFiles(commitFiles, stepDir);
      const entries = await Promise.all(
        testNames.map(
          async (name) =>
            [
              name,
              await testIoHashFromFiles(commitFiles, stepDir, name),
            ] as const
        )
      );
      if (!cancelled) setHashes({ codeHash, ioHashByName: new Map(entries) });
    })();
    return () => {
      cancelled = true;
    };
  }, [commitFiles, stepDir, testNames]);

  return useMemo(() => {
    if (!hashes) {
      const rows = testNames.map<StepTestRow>((name) => ({
        name,
        status: "notrun",
        message: null,
      }));
      return {
        rows,
        rollup: rollupStepTestStatus(rows),
        couldNotRun: false,
        ready: false,
      };
    }
    const { rows, latest } = computeStepTestRows({
      testNames,
      codeHash: hashes.codeHash,
      ioHashByName: hashes.ioHashByName,
      executions: stepExecutions,
    });
    const couldNotRun =
      latest?.status === "error" && latest.testSpecs.length === 0;
    return {
      rows,
      rollup: rollupStepTestStatus(rows),
      couldNotRun,
      ready: true,
    };
  }, [hashes, testNames, stepExecutions]);
}
